import { getPassengersAndRoomFromQuery } from "@website/accommodation-ui/helpers/passengers";
import { createLink, getPriceStatus, getStayCount } from "@website/accommodation-ui/price-calendar-wrapper/helpers";
import { PriceCalendarItem, PriceCalendarProperties } from "@website/accommodation-ui/price-calendar-wrapper/types";
import { USER_TRACKING_EVENTS, WEBENGAGE_EVENTS } from "@website/tracking/tracking.types";
import { addEventToWebengage } from "@website/tracking/tracking.webengage";
import { addEventToTracking } from "@website/tracking/user";
import { daysBetweenDates } from "@website/utils/date-format";
import throttle from "lodash-es/throttle";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
export const isTodayPrice = (calendarDate: string, departureDate: string) => new Date(departureDate).getTime() === new Date(calendarDate).getTime();
export const usePriceCalendar = (properties: PriceCalendarProperties) => {
  const {
    list,
    solutionCount,
    dest,
    origin,
    stDate,
    rtDate
  } = properties;
  const router = useRouter();
  const sliderWrapper = useRef<HTMLDivElement>(null);
  const slider = useRef<HTMLElement>(null);
  const [sliderTranslate, setSliderTranslate] = useState(0);
  const [isForwardDisabled, setIsForwardDisabled] = useState(false);
  const isBackwardDisabled = useRef(false);
  const {
    highestPrice,
    lowestPrice
  } = (() => {
    const listWithPrice = list?.filter(item => Boolean(Number(item.minPrice)));
    const sortedList = listWithPrice?.sort((a, b) => a.minPrice - b.minPrice);
    return {
      lowestPrice: sortedList?.[0]?.minPrice,
      highestPrice: sortedList?.at(-1)?.minPrice
    };
  })();
  const todayPriceItemIndex = list?.findIndex(item_0 => isTodayPrice(item_0.departureDate, stDate)) || 0;
  const handlePriceBoxClick = (item_1: PriceCalendarItem, link: ReturnType<typeof createLink>) => {
    const {
      search_source,
      calendar_type,
      passengers: passengersQuery
    } = router.query;
    const {
      rooms,
      adults,
      child
    } = getPassengersAndRoomFromQuery(passengersQuery as string);
    const stayCount = getStayCount(stDate as string, rtDate as string);
    const daysAway = daysBetweenDates(new Date(stDate as string), new Date(item_1.date));
    const priceDifference = (list?.[todayPriceItemIndex]?.minPrice ?? 0) - item_1.price;
    addEventToTracking(USER_TRACKING_EVENTS.PRICING_TABLE_CLICKED, {
      product: "tour",
      origin_city: origin as string,
      destination_city: dest as string,
      departure_date: new Date(stDate as string),
      return_departure_date: new Date(rtDate as string),
      search_source: search_source as string,
      number_of_passengers: adults + child,
      number_of_rooms: rooms.length,
      calendar_type: calendar_type as string,
      number_of_results: solutionCount as number,
      nights: stayCount,
      price_difference: priceDifference,
      days_away: daysAway
    });
    addEventToWebengage(WEBENGAGE_EVENTS.TOUR_ST_PRICING_TABLE_CLICKED, {
      Origin: origin,
      Destination: dest,
      NumberOfResult: solutionCount,
      Nights: stayCount,
      DaysAway: daysAway,
      PriceDifference: priceDifference
    });
    router.push(link);
  };
  const formattedList: Array<PriceCalendarItem> | undefined = list?.map((item_2, index) => ({
    date: item_2.departureDate,
    price: item_2.minPrice,
    isTodayPrice: index === todayPriceItemIndex,
    status: getPriceStatus(item_2, highestPrice, lowestPrice),
    onClick: priceItem => handlePriceBoxClick(priceItem, createLink(item_2, router.query))
  }));
  useEffect(() => {
    const sliderReference = slider.current;
    const sliderWrapperReference = sliderWrapper.current;
    if (!sliderReference || !sliderWrapperReference || !todayPriceItemIndex) return;
    const targetElement = sliderWrapperReference.children[todayPriceItemIndex] as HTMLElement;
    const valueToSlide = targetElement?.getBoundingClientRect()?.left - sliderReference?.getBoundingClientRect()?.left;
    if (valueToSlide < 0) {
      setSliderTranslate(Math.abs(valueToSlide));
    }
  }, [todayPriceItemIndex]);
  useEffect(() => {
    const resizeListener = () => {
      if (typeof window === "undefined") return;
      const sliderWrapperReference_0 = sliderWrapper.current;
      const sliderReference_0 = slider.current;
      if (!sliderWrapperReference_0 || !sliderReference_0) return;
      const valueToSlide_0 = sliderWrapperReference_0?.getBoundingClientRect()?.left - sliderReference_0?.getBoundingClientRect()?.left;
      if (valueToSlide_0 > 0) {
        setSliderTranslate(previous => previous - valueToSlide_0);
      } else {
        setIsForwardDisabled(false);
      }
    };
    const resizeListenerThrottled = throttle(resizeListener);
    resizeListener();
    window.addEventListener("resize", resizeListenerThrottled);
    return () => {
      window.removeEventListener("resize", resizeListenerThrottled);
    };
  }, []);
  const getElementWidth = (element: Element) => {
    if (!element) return 0;
    const style = getComputedStyle(element);
    const marginLeft = Number.parseFloat(style.marginLeft); // Left margin
    return element?.getBoundingClientRect().width + marginLeft * 2;
  };
  const onForwardClicked = () => {
    const sliderWrapperReference_1 = sliderWrapper.current;
    const sliderReference_1 = slider.current;
    if (!sliderWrapperReference_1 || !sliderReference_1) return;
    const element_0 = sliderWrapperReference_1.children?.[0];
    if (!element_0) return;
    const childRect = sliderWrapperReference_1?.getBoundingClientRect();
    const parentRect = sliderReference_1?.getBoundingClientRect();
    const elementsWidth = getElementWidth(element_0);
    const leftDistance = childRect?.left - parentRect?.left;
    isBackwardDisabled.current = false;
    if (Math.abs(leftDistance) <= elementsWidth) {
      setIsForwardDisabled(true);
    }
    setSliderTranslate(previous_0 => {
      if (Math.abs(leftDistance) <= elementsWidth) {
        return previous_0 + Math.abs(leftDistance);
      }
      return previous_0 + elementsWidth;
    });
  };
  const onBackwardClicked = () => {
    const sliderWrapperReference_2 = sliderWrapper.current;
    if (!sliderWrapperReference_2) return;
    const element_1 = sliderWrapperReference_2.children?.[0];
    if (!element_1) return;
    const elementsWidth_0 = getElementWidth(element_1);
    setIsForwardDisabled(false);
    isBackwardDisabled.current = sliderTranslate - elementsWidth_0 <= 0;
    setSliderTranslate(previous_1 => {
      const calculatedValue = previous_1 - elementsWidth_0;
      return calculatedValue <= 0 ? 0 : calculatedValue;
    });
  };
  return {
    sliderWrapper,
    listTranslate: sliderTranslate,
    slider,
    list: formattedList,
    onForwardClicked,
    onBackwardClicked,
    isForwardDisabled,
    isBackwardDisabled: isBackwardDisabled.current
  };
};